import React, { Component, Fragment } from 'react'
import { arrayOf, shape, string, func } from 'prop-types'
import { hasTag } from '@ycos/coremedia-renderer'
import { getFontStyles } from '@ycos/primitives'
import { createComponent } from '@ycos/fela'
import { Lock } from './lock'

class Accordion extends Component {
  constructor(props) {
    super(props)

    this.hasBorderBottom = hasTag(props.item.tags, 'Accordion.hasBorderBottom')
    this.state = {
      activeItem: hasTag(props.item.tags, 'accordionOpenByDefault') ? 0 : null
    }
  }

  toggleOpenClose(itemIndex, teaserTitle, onDropdownClick) {
    const { activeItem } = this.state
    const { onAccordionOpen } = this.props
    const active = activeItem !== itemIndex
    onDropdownClick()
    if (active && onAccordionOpen) onAccordionOpen(teaserTitle)
    this.setState({ activeItem: activeItem === itemIndex ? null : itemIndex })
  }

  render() {
    const { children, item, onDropdownClick = () => {} } = this.props
    const { teaserTitle, tags } = item
    const hasPaddingBottom = hasTag(tags, 'Accordion.hasPaddingBottom')
    const isLoyaltyStyle = hasTag(tags, 'Accordion.loyaltyStyle')

    return (
      <div data-automation="accordion">
        {teaserTitle && <AccordionTitle>{teaserTitle}</AccordionTitle>}
        {children.map(({ props }, i) => {
          const { item, children } = props
          const hasChildren = item.items?.length > 0
          const { activeItem } = this.state
          return (
            <AccordionItem key={i} data-automation="accordion-item" data-cm-metadata={item.dataCmMetadata} hasBorderBottom={this.hasBorderBottom} isLoyaltyStyle={isLoyaltyStyle}>
              <HeadingSmallStyled
                isLoyaltyStyle={isLoyaltyStyle}
                onClick={this.toggleOpenClose.bind(this, i, item.teaserTitle, onDropdownClick)}
                isActive={activeItem === i}
                hasChildren={hasChildren}
              >
                {item.teaserTitle}
              </HeadingSmallStyled>
              {!hasChildren && <Lock />}
              <ItemContent isActive={activeItem === i} data-automation="accordion-item-content" hasPaddingBottom={hasPaddingBottom}>
                <Fragment>{children}</Fragment>
              </ItemContent>
            </AccordionItem>
          )
        })}
      </div>
    )
  }

  static layoutVariant = ['isc-accordion']
}

Accordion.propTypes = {
  onAccordionOpen: func,
  children: arrayOf(
    shape({
      item: shape({
        teaserTitle: string.isRequired,
        dataCmMetadata: string
      }),
      children: arrayOf(shape()).isRequired
    })
  ).isRequired
}

const AccordionItem = createComponent(
  'AccordionItem',
  ({ theme, hasBorderBottom, isLoyaltyStyle }) => {
    const optionalStyle = isLoyaltyStyle ? {
      padding: '0 24px',
      marginBottom: '8px',
      '& p': {
        fontSize: '14px',
        lineHeight: '21px',
        fontWeight: '325'
      },
    } : {
      borderTop: `1px solid ${theme.custom.accordion.lineColor}`,
      ':last-child ': {
        borderBottom: hasBorderBottom ? `1px solid ${theme.custom.accordion.lineColor}` : undefined
      }
    }
    return ({
    backgroundColor: isLoyaltyStyle ? theme.typography.colors.PrimaryGrey : 'none',
    position: 'relative',
    ...optionalStyle
  })},
  'div'
)

const HeadingSmallStyled = createComponent(
  'HeadingSmallStyled',
  ({ theme, isActive, hasChildren, isLoyaltyStyle }) => {
    const { spacingMultiplier, custom, typography } = theme
    const { colors } = typography
    const { color } = custom.accordion
    const padY = 3 * spacingMultiplier
    const padR = 5 * spacingMultiplier

    const fontStyle = getFontStyles(typography, { type: 'Heading', name: 'B4' });
    return {
      direction: 'ltr',
      ...fontStyle,
      fontWeight: isLoyaltyStyle ? custom.accordion.loyaltyFontStyle?.fontWeight : fontStyle.fontWeight,
      fontSize: isLoyaltyStyle ? custom.accordion.loyaltyFontStyle?.fontSize : fontStyle.fontSize,
      lineHeight: isLoyaltyStyle ? custom.accordion.loyaltyFontStyle?.lineHeight : fontStyle.lineHeight,
      ...(isLoyaltyStyle ? custom.accordion.loyaltyFontStyle : {}),
      color: hasChildren ? color : colors.SecondaryGreySky,
      padding: `${padY}px ${padR}px ${padY}px 0`,
      margin: 0,
      cursor: 'pointer',
      position: 'relative',
      textAlign: 'left',
      pointerEvents: hasChildren ? 'auto' : 'none',
      ':after': hasChildren
        ? {
          content: '""',
          width: '9px',
          height: '9px',
          borderTop: `1px solid ${color}`,
          borderRight: `1px solid ${color}`,
          position: 'absolute',
          right: '2px',
          top: isActive ? '46%' : 'calc(46% - 5px)',
          transform: isActive ? 'rotate(-45deg)' : 'rotate(135deg)'
        }
        : undefined
    }
  },
  'h2'
)

const ItemContent = createComponent(
  'ItemContent',
  ({ isActive, hasPaddingBottom }) => ({
    overflow: 'hidden',
    maxHeight: isActive ? 'auto' : '0',
    transition: isActive ? 'max-height .3s ease-in-out, margin .1s ease-in-out' : 'none',
    paddingBottom: isActive && hasPaddingBottom ? '24px' : '0'
  }),
  'div'
)

const AccordionTitle = createComponent(
  'AccordionTitle',
  ({ theme }) => {
    const { spacingMultiplier, custom } = theme
    return {
      direction: 'ltr',
      fontFamily: custom.accordion.fontFamily,
      fontWeight: custom.accordion.fontWeight,
      fontSize: custom.accordion.fontSize,
      lineHeight: '1.3333333333333333',
      textTransform: 'uppercase',
      color: '#111111',
      marginTop: `${6 * spacingMultiplier}px`,
      marginBottom: `${4 * spacingMultiplier}px`,
      'screen-large': {
        marginTop: 0
      }
    }
  },
  'h2'
)

export default Accordion
